import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Subscription, Subject } from 'rxjs';

import { TamDashboardActions, TamDashboardEvents } from '../tam-dashboard.model';
import { filter } from 'rxjs/operators';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { TamDashboardPieChartTileConfiguration } from './tam-dashboard-pie-chart-tile.config';
import { ChartComponent } from '@progress/kendo-angular-charts';

@Component({
    selector: 'tam-dashboard-pie-chart-tile',
    templateUrl: './tam-dashboard-pie-chart-tile.component.html',
    styleUrls: ['./tam-dashboard-pie-chart-tile.component.scss']
})
export class TamDashboardPieChartTileComponent implements OnInit, AfterViewInit, OnDestroy, TamDashboardTileBaseComponent {
    @ViewChild('chart', { static: false }) private _chart: ChartComponent;
    data: any;
    seriesColors = ['#63b9bf', '#da8945', '#e6be0a', '#bdd344', '#cc2f64', '#d248bd', '#784f9b'];
    legendLabels = {
        font: '13px Arial, sans-serif',
        content: (e) => this._config.legendLabedContent(e)
    };

    private _destroySubscriptions: Array<Subscription> = [];
    private _config: TamDashboardPieChartTileConfiguration;
    private _matchPrint: MediaQueryList = window.matchMedia('print');
    private _afterViewFeedback$ = new Subject<string>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        // safari not support addEventListener, support addListener
        if (this._matchPrint) {
            if (this._matchPrint.addEventListener) {
                this._matchPrint.addEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['addListener'](this._resizeChart);
            }
        }
    }

    ngAfterViewInit(): void {
        this._afterViewFeedback$.next(TamDashboardEvents.TILE_AFTER_VIEW_INIT);
    }

    ngOnDestroy() {
        if (this._matchPrint) {
            if (this._matchPrint.removeEventListener) {
                this._matchPrint.removeEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['removeListener'](this._resizeChart);
            }
            this._resizeChart = null;
        }
        if (this._destroySubscriptions && this._destroySubscriptions.length > 0) {
            this._destroySubscriptions.forEach(element => {
                if (element) {
                    element.unsubscribe();
                }
            });
            this._destroySubscriptions = [];
        }

        if (this._config) {
            this._config.destroy();
        }
    }

    setConfig(config: TamDashboardPieChartTileConfiguration): boolean {
        /*
            fix bug: TAM-29118, has only one new config subscribe event
            dashboard view page -> change focus/refresh tile -> new config -> config init() -> config setConfig
            confirm the component every use the new config, not old config
            confirm the component every call new config function, example this._config.labelContent
        */
        this._config = config;
        this._init();
        return true;
    }

    labelContent = (e: any) => {
        if (this._config && this._config.labelContent) {
            // a workaround to get the real value from config
            let data = this._config.data?.filter(item => item.value === e.value.toString())[0];
            if (!data) {
                data = this._config.data?.filter(item => item.value === '-' + e.value.toString())[0];
            }
            if (data) {
                e.value = data.value;
            }
            return this._config.labelContent(e);
        }
        return e.value;
    };

    onSeriesClick(event) {
        if (this._config && this._config.onSeriesClick) {
            this._config.onSeriesClick(event);
        }
    }

    private _init() {
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        this._destroySubscriptions.push(
            this._config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this._config.id),
            ).subscribe(action => this._onSubscribeActions(action)),
            this._afterViewFeedback$.subscribe((payload) => {
                if (payload === TamDashboardEvents.TILE_AFTER_VIEW_INIT) {
                    this._config.feedbackSubject$.next({
                        type: TamDashboardEvents.TILE_AFTER_VIEW_INIT,
                        id: this._config.id
                    });
                }
            })
        );
    }

    private _resizeChart = (mql) => {
        if (mql.matches) {
            this._chart.resize();
        }
    };

    private _onSubscribeActions(action) {
        if (action.type === TamDashboardActions.SET_TILE_DATA) {
            if (!Array.isArray(action.payload)) {
                return;
            }
            if (action.payload) {
                this.data = action.payload.map(item => ({
                    category: item.category,
                    value: item.value,
                    valueText: item.valueText
                }));
            } else {
                this.data = action.payload;
            }
            this._config.feedbackSubject$.next({
                type: TamDashboardEvents.TILE_LOADED_DATA,
                id: this._config.id
            });
            this._changeDetectorRef.detectChanges();
        }
    }
}
